import { useEffect, useRef, useState } from "react";
import { shouldShowMediaFallback } from "../MediaFallback/shouldShowFallback";

// Bounded retry-then-fallback for a media <img>. Extracted out of ImageBlock
// so the retry state machine (fires an image `error` -> retries with backoff
// -> permanent fallback once exhausted) is testable and reusable on its own,
// and so ImageBlock itself stays under fallow's complexity threshold.
//
// Root cause this exists to cover (QA sheet row 19, measured on staging
// 2026-07-22): a freshly-uploaded image can 404/error on its very first paint
// — S3/CDN read-after-write propagation lag — even though the exact same URL
// loads fine moments later. Without a retry, the FIRST error permanently
// swapped in the "Image unavailable" placeholder with no way to recover short
// of the user removing and re-adding the block.
export const MAX_MEDIA_LOAD_RETRIES = 2;
const RETRY_DELAY_MS = 700;

function useMediaLoadRetry({ src, isGiphyOrGif }) {
  const [imageLoaded, setImageLoaded] = useState(!isGiphyOrGif);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef();
  const isFirstRender = useRef(true);
  const retryTimeoutRef = useRef(null);

  // Reset imageLoaded/error/retry state when src changes, skip on first mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setHasError(false);
    setRetryCount(0);
    setImageLoaded(!isGiphyOrGif ? true : false);
  }, [src, isGiphyOrGif]);

  // Clear any pending retry timer on unmount / src change.
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [src]);

  // Attach load/error listeners manually via imgRef.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleLoad = () => {
      if (isGiphyOrGif) setImageLoaded(true);
    };

    const handleError = () => {
      if (isGiphyOrGif) setImageLoaded(true);
      // Empty/whitespace src is "no media selected yet", not an error.
      if (!shouldShowMediaFallback(src)) return;
      if (retryCount < MAX_MEDIA_LOAD_RETRIES) {
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount((c) => c + 1);
        }, RETRY_DELAY_MS * (retryCount + 1));
        return;
      }
      setHasError(true);
    };

    img.addEventListener("load", handleLoad);
    img.addEventListener("error", handleError);
    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [src, isGiphyOrGif, retryCount]);

  // A non-zero retryCount busts the src so the <img> issues a genuinely new
  // request (not a browser-cached repeat of the same failed one).
  const uniqueSrc = isGiphyOrGif
    ? `${src}?t=${Date.now()}`
    : retryCount > 0 && src
    ? `${src}${src.includes("?") ? "&" : "?"}_retry=${retryCount}`
    : src;

  // Inline guard so a broken asset swaps to the clean placeholder even if the
  // manual listener above missed the event.
  const onImgError = () => {
    if (!shouldShowMediaFallback(src)) return;
    if (retryCount >= MAX_MEDIA_LOAD_RETRIES) setHasError(true);
  };

  return { imgRef, imageLoaded, setImageLoaded, hasError, uniqueSrc, onImgError };
}

export default useMediaLoadRetry;
